import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Drawer from '~/components/Drawer'
import DrawerItem from '~/components/DrawerItem'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Textarea from '~/components/Textarea'

const DrawerPage: Component = () => {
  const [basicOpen, setBasicOpen] = createSignal(false)
  const [leftOpen, setLeftOpen] = createSignal(false)
  const [topOpen, setTopOpen] = createSignal(false)
  const [bottomOpen, setBottomOpen] = createSignal(false)
  const [navOpen, setNavOpen] = createSignal(false)
  const [formOpen, setFormOpen] = createSignal(false)

  return (
    <>
      <Title>Drawer Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Drawer</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Paneles laterales y de navegación. El Drawer (o cajón) es una superficie interactiva que desliza desde los bordes de la pantalla, ideal para menús de navegación, formularios laterales o
            filtros.
          </p>
        </div>

        <div class="space-y-12">
          {/* Posiciones e Interacción */}
          <Card>
            <Card.Header
              title="Puntos de Entrada"
              subtitle="Controla desde dónde aparece el panel: derecha (default), izquierda, arriba o abajo."
              icon={<Icon icon="material-symbols-light:chevron-right" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 p-10 bg-surface-muted rounded-xl border border-border">
                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-primary-500">Activadores de Posición</h4>
                  <div class="flex flex-wrap gap-4 px-3">
                    <Button color="primary" variant="contained" onClick={() => setBasicOpen(true)}>
                      Derecha (Standard)
                    </Button>
                    <Button color="secondary" onClick={() => setLeftOpen(true)}>
                      Izquierda
                    </Button>
                    <Button color="info" variant="outlined" onClick={() => setTopOpen(true)}>
                      Superior
                    </Button>
                    <Button color="warning" variant="outlined" onClick={() => setBottomOpen(true)}>
                      Inferior
                    </Button>
                  </div>
                </div>

                <div class="space-y-4">
                  <p class="text-sm text-foreground-subtle italic">Haz clic en los botones para visualizar las diferentes animaciones de entrada y salida.</p>
                  <div class="p-6 bg-surface rounded-xl shadow-sm border border-border">
                    <code class="text-xs font-mono text-primary-600">{'<Drawer position="right" ... />'}</code>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Drawer como Navegación */}
          <Card>
            <Card.Header
              title="Navegación Multinivel"
              subtitle="Usa DrawerItem para construir menús complejos con submenús, iconos y badges."
              icon={<Icon icon="material-symbols-light:list" class="text-indigo-500" size={24} />}
            />
            <Card.Body>
              <div class="p-8 bg-surface-muted rounded-xl border border-border flex flex-col items-center">
                <Button color="secondary" variant="contained" size="lg" icon="material-symbols-light:menu" onClick={() => setNavOpen(true)}>
                  Probar Menú de Navegación
                </Button>

                <Drawer isOpen={navOpen()} onClose={() => setNavOpen(false)} title="Explorar Sistema" size="sm">
                  <div class="p-4 space-y-3">
                    <DrawerItem icon="material-symbols-light:home-outline" title="Dashboard" to="/" />
                    <DrawerItem icon="material-symbols-light:database-outline" title="Inventario" subtitle="Gestión de productos" badge={{ count: 12, color: 'success' }} />
                    <DrawerItem
                      icon="material-symbols-light:settings-outline"
                      title="Configuraciones"
                      submenu={[
                        { label: 'Perfil Usuario', to: '/config/profile', icon: 'material-symbols-light:person-outline' },
                        { label: 'Notificaciones', to: '/config/notify', badge: { dot: true, color: 'danger' } },
                        { label: 'API Keys', href: '/config/api' }
                      ]}
                    />
                    <div class="pt-10">
                      <Button class="w-full" color="danger" variant="soft" icon="material-symbols-light:logout" onClick={() => setNavOpen(false)}>
                        Cerrar Sesión
                      </Button>
                    </div>
                  </div>
                </Drawer>
              </div>
            </Card.Body>
          </Card>

          {/* Edición Lateral (Forms) */}
          <Card>
            <Card.Header
              title="Edición y Creación"
              subtitle="Aprovecha el espacio vertical para formularios de configuración sin perder el contexto de la página."
              icon={<Icon icon="material-symbols-light:edit-square-outline" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div class="space-y-6">
                  <h3 class="text-2xl font-black text-foreground italic tracking-tighter">CONFIGURACIÓN RÁPIDA</h3>
                  <p class="text-foreground-subtle leading-relaxed text-balance">
                    Los Drawers son ideales para editar detalles de un registro en una tabla sin navegar a otra página. Esto mantiene la fluidez del flujo de trabajo del usuario.
                  </p>
                  <Button color="primary" icon="material-symbols-light:person-add-outline" onClick={() => setFormOpen(true)}>
                    Añadir Colaborador
                  </Button>
                </div>

                <div class="relative p-1 border-2 border-border rounded-3xl overflow-hidden grayscale opacity-50 select-none">
                  <div class="bg-surface p-6 flex flex-col gap-4">
                    <div class="w-full h-8 bg-surface-muted rounded animate-pulse" />
                    <div class="w-2/3 h-8 bg-surface-muted rounded animate-pulse" />
                    <div class="w-full h-32 bg-surface-muted rounded animate-pulse mt-4" />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Parámetros técnicos para la personalización de Drawer."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">isOpen</td>
                      <td class="px-6 py-4 italic">boolean</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 font-bold text-primary-600">MANDATORIO. Estado de visibilidad.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">onClose</td>
                      <td class="px-6 py-4 italic">() =&gt; void</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic text-balance font-bold text-primary-600">MANDATORIO. Callback disparado al cerrar el panel.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">position</td>
                      <td class="px-6 py-4 italic">'left' | 'right' | 'top' | 'bottom'</td>
                      <td class="px-6 py-4 font-mono">'right'</td>
                      <td class="px-6 py-4 italic">Origen del deslizamiento.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">size</td>
                      <td class="px-6 py-4 italic">'xs' | 'sm' | 'md' | 'lg' | 'xl'</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4 text-balance">Ancho (o alto) máximo del panel.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">headerContent</td>
                      <td class="px-6 py-4 italic text-xs">JSX.Element</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Sobrescribe el encabezado por defecto con uno custom.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>

      {/* RENDERIZADO DE DRAWERS OCULTOS */}
      <Drawer isOpen={basicOpen()} onClose={() => setBasicOpen(false)} title="Configuración de Sistema" position="right">
        <div class="p-6 space-y-6">
          <p class="text-sm text-foreground-subtle">Panel derecho estándar con ancho adaptativo.</p>
          <div class="p-4 bg-surface-muted rounded-xl space-y-4">
            <Badge color="info">ID: 40293</Badge>
            <Input label="Host Name" value="FJ_PROD_SERVER" />
            <Button class="w-full">Guardar Cambios</Button>
          </div>
        </div>
      </Drawer>

      <Drawer isOpen={leftOpen()} onClose={() => setLeftOpen(false)} title="Herramientas" position="left" size="sm">
        <div class="p-4 space-y-2">
          <DrawerItem icon="material-symbols-light:edit-outline" title="Editor" />
          <DrawerItem icon="material-symbols-light:brush-outline" title="Temas" />
          <DrawerItem icon="material-symbols-light:code" title="Source" />
        </div>
      </Drawer>

      <Drawer isOpen={topOpen()} onClose={() => setTopOpen(false)} title="Notificaciones Pendientes" position="top" size="sm">
        <div class="p-6 text-center space-y-4">
          <Icon icon="material-symbols-light:notifications-outline" class="text-amber-500 mx-auto" size={48} />
          <h4 class="font-bold">No hay alertas nuevas</h4>
          <p class="text-xs text-foreground-subtle">Te avisaremos cuando ocurra algo importante.</p>
        </div>
      </Drawer>

      <Drawer isOpen={bottomOpen()} onClose={() => setBottomOpen(false)} title="Resumen de Acción" position="bottom" size="sm">
        <div class="p-8 flex flex-col items-center gap-4">
          <p class="text-lg font-bold">¿Deseas exportar los datos?</p>
          <div class="flex gap-2">
            <Button variant="contained">Exportar CSV</Button>
            <Button variant="outlined" onClick={() => setBottomOpen(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Drawer>

      <Drawer isOpen={formOpen()} onClose={() => setFormOpen(false)} title="Nuevo Colaborador" size="md">
        <div class="p-6">
          <Form
            class="space-y-6"
            onSubmit={_data => {
              setFormOpen(false)
            }}
          >
            <div class="flex flex-col items-center gap-2 mb-6">
              <Badge class="bg-indigo-500/10 text-indigo-600 p-6 rounded-full">
                <Icon icon="material-symbols-light:person-add-outline" size={32} />
              </Badge>
              <h4 class="font-bold">Información de Perfil</h4>
            </div>
            <Input label="Nombre Completo" placeholder="Ej: Juan Pérez" startIcon="material-symbols-light:person-outline" />
            <Input label="Correo Corporativo" placeholder="juan@florezjaramillo.com" startIcon="material-symbols-light:mail-outline" />
            <Textarea label="Bio Corta" placeholder="Escribe algo sobre el nuevo integrante..." />
            <div class="pt-6 border-t border-border flex gap-2">
              <Button type="submit" variant="contained" color="primary" pill class="flex-1">
                Registrar
              </Button>
              <Button variant="text" onClick={() => setFormOpen(false)}>
                Descartar
              </Button>
            </div>
          </Form>
        </div>
      </Drawer>
    </>
  )
}

export default DrawerPage
